import { SceneSprite } from "GameObjects/StoryGame/SceneSprite";
import { Expo, TimelineLite, TimelineMax } from "gsap";
import * as HLF from "html-living-framework";
import { Application } from "Singletons/Application";
import { AbstractStoryState } from "States/AbstractStoryState";
import * as Topics from "Data/Topics";

import { ch3_p03 } from "../../../layouts/ch3_p03/ch3_p03"
import { list } from "Utils/Reflection";
import { random } from "html-living-framework/lib/Utils/ArrayUtils";

export class Chapter3Page3 extends AbstractStoryState {

    readonly panContainer = this.addChild(new PIXI.Container());

    readonly bg = list(()=>this.panContainer.addChildAt(new SceneSprite(), 0));
    readonly bedMessy = this.panContainer.addChild(new SceneSprite());
    readonly bedClean = this.panContainer.addChild(new SceneSprite());
    readonly nightstandOpen = this.panContainer.addChild(new SceneSprite());
    readonly nightstandClosed = this.panContainer.addChild(new SceneSprite());
    readonly clothingUp = this.panContainer.addChild(SceneSprite.withPrefix("ch3_p03/"));
    readonly clothingDown = this.panContainer.addChild(SceneSprite.withPrefix("ch3_p03/"));
    readonly floorItemsUp = this.panContainer.addChild(SceneSprite.withPrefix("ch3_p03/"));
    readonly floorItemsDown = this.panContainer.addChild(SceneSprite.withPrefix("ch3_p03/"));
    readonly basket = this.panContainer.addChild(SceneSprite.withPrefix("ch3_p03/"));
    readonly basketLidOff = this.panContainer.addChild(SceneSprite.withPrefix("ch3_p03/"));
    readonly basketLidOn = this.panContainer.addChild(SceneSprite.withPrefix("ch3_p03/"));
    readonly guitar = this.panContainer.addChild(new SceneSprite());

    readonly frameLeft = this.addChild(SceneSprite.withPrefix("ch3_p03/"));
    readonly frameRight = this.addChild(SceneSprite.withPrefix("ch3_p03/"));
    readonly linesLeft = this.panContainer.addChild(new SceneSprite());

    public tidied: { [key: string]: boolean } = {
        'bed': false,
        'clothing': false,
        'flooritems': false,
    };

    private readonly audioLayers: HLF.IAudioTrack[] = [];

    public init(pSize: PIXI.Point, pData: any) {
        // exported layouts from Animate CC
        ch3_p03.call(this);
        this.bedClean.visible = false; // starts hidden
        this.basketLidOn.visible = false; // starts hidden
        this.nightstandClosed.visible = false; // starts hidden

        this.clothingUp.visible = false; // unused
        this.floorItemsUp.visible = false; // unused

        this.frameLeft.visible = false; // unused
        this.frameRight.visible = false; // unused

        // interactivity
        this._subscriptions.push([
            PubSub.subscribe('tidy', this.onTidyItemClicked.bind(this)),
            PubSub.subscribe('tidied', this.onTidyUpdate.bind(this)),
        ]);

        this.makeToggleSource(this.bedMessy, 'bed');
        this.makeToggleTarget(this.bedClean, 'bed');

        this.makeToggleSource(this.clothingDown, 'clothing');
        this.makeToggleTarget(this.basketLidOn, 'clothing'); // starts hidden
        this.makeToggleTarget(this.basketLidOff, 'clothing'); // starts visible

        this.makeToggleSource(this.floorItemsDown, 'flooritems');
        this.makeToggleTarget(this.nightstandOpen, 'flooritems'); // starts visible
        this.makeToggleTarget(this.nightstandClosed, 'flooritems'); // starts hidden

        this.audioLayers.push(
            this.getOrCreateAudioTrack("HH_Ch3_Loop_Bassoon_LAYER_2"),
            this.getOrCreateAudioTrack("HH_Ch3_Loop_AC_GTR_LAYER_3"),
            this.getOrCreateAudioTrack("HH_Ch3_Loop_PERC_LAYER_4"),
            this.getOrCreateAudioTrack("HH_Ch3_Loop_MEL_PERC_LAYER_5"),
            this.getOrCreateAudioTrack("HH_Ch3_Loop_WW_LAYER_6"),
        );

        this.bedMessy.hitArea = new HLF.Input.PixelPerfectHitArea(Application.instance.renderer, this.bedMessy);

        // super.init calls onResize
        super.init(pSize, pData);
    }

    public update(pDeltaTime: number) {
        super.update(pDeltaTime);
    }

    public animateIn(pOnComplete: () => void) {
        super.animateIn(pOnComplete);
    }

    protected onTidyItemClicked(pTopic: string, pData: string): void {
        this.tidied[pData] = true;
        const tidySound = random([
            "HH_Ch3_Clothing_Pick_Up_3_FX",
            "HH_Ch3_Clothing_Pick_Up_4_FX"
        ]);
        Application.instance.audio.play(tidySound, 1, false, HLF.AudioCategory.SFX);
        PubSub.publishSync('tidied', this.tidied);
    }

    protected onTidyUpdate(pTopic: string, pData: { [key: string]: boolean }): void {

        const baseTrack = this.getOrCreateAudioTrack("HH_Ch3_Loop_LAYER1");
        if (!baseTrack.isPlaying) {
            baseTrack.play();
            baseTrack.setLooped(true);
        }

        const tidyCount = Object.values(pData).reduce((count, val) => count += val ? 1 : 0, 0);
        for (let i = 0; i < tidyCount; i++) {
            const layer = this.audioLayers[i];
            if (!layer.isPlaying()) {
                layer.setVolume(0);
                layer.setLooped(true);
                layer.play();
                layer.fadeTo(1, 500);
            }
            layer.setTimePos(baseTrack.getTimePos());
        }

        // if all objects are tidied
        if (!Object.values(pData).some(c => !c)) {
            this.onAllObjectsTidied();
        }
    }

    protected onAllObjectsTidied() {
        PubSub.publishSync(Topics.HIDE_ALL_HINTS, undefined);

        // const bgWidth = this.bg.reduce((w, bg)=>{return w + bg.width}, 0);
        this._tutorialHand?.setParent(this.panContainer);

        new TimelineLite()
            .to(this.panContainer, 2, {
                pixi: {x: (this._size.x - this.panContainer.width) / 2},
                ease: Expo.easeInOut,
            })
            .delay(1.5)
        ;

        this.showHint(this.guitar, "hand", 8);

        this.guitar.interactive = true;
        this.guitar.buttonMode = true;
        this.guitar.accessible = true;

        this.guitar.addListener(HLF.Input.Events.POINTER_TAP, ()=>{
            this.gotoNextState({ transition: "left" });
        });
    }

    // click to hide object, and toggle others
    private makeToggleSource(source: SceneSprite, id: string) {
        source.interactive = true;
        source.accessible = true;
        source.buttonMode = true;
        source.visible = true;
        source.addListener(HLF.Input.Events.POINTER_TAP, e => {
            source.interactive = false;
            source.accessible = false;
            source.buttonMode = false;
            source.FadeOut();
            PubSub.publishSync(`tidy.${id}`, id);
            PubSub.publishSync('tidy', id);
        });
    }

    // toggle object visibility
    private makeToggleTarget(target: SceneSprite, id: string) {
        PubSub.subscribeOnce(`tidy.${id}`, () => {
            if (target.visible) {
                target.FadeOut();
            }
            else {
                target.FadeIn();
            }
        });
    }

    private getOrCreateAudioTrack (trackName: string) {
        return HLF.Application.instance.audio.getAudioTrack(trackName, HLF.AudioCategory.BGM)
            ?? HLF.Application.instance.audio.createAudioTrack(trackName, HLF.AudioCategory.BGM);
    }
}